import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  SquaresFour, 
  Buildings, 
  Truck,
  ShoppingCart,
  Receipt,
  Bank,
  ChartBar,
  CheckSquare, 
  FileText, 
  FolderOpen, 
  ListChecks,
  Gear,
  SignOut,
  User,
  Files,
  Warning,
  ClockClockwise,
  Sparkle
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const navigation = [
  {
    section: 'Overview',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: SquaresFour },
      { name: 'AI Assist', href: '/ai-assist', icon: Sparkle },
    ]
  },
  {
    section: 'Relationships',
    items: [
      { name: 'CRM', href: '/crm', icon: Buildings },
      { name: 'Suppliers', href: '/suppliers', icon: Truck },
    ]
  },
  {
    section: 'Financial',
    items: [
      { name: 'Invoices', href: '/invoices', icon: FileText },
      { name: 'Purchase Orders', href: '/purchase-orders', icon: ShoppingCart },
      { name: 'POPs', href: '/pops', icon: Receipt },
      { name: 'Bank Statements', href: '/bank-statements', icon: Bank },
      { name: 'Bookkeeping', href: '/bookkeeping', icon: ChartBar },
      { name: 'Anomalies', href: '/anomalies', icon: Warning },
    ]
  },
  {
    section: 'Documents',
    items: [
      { name: 'Documents', href: '/documents', icon: FolderOpen },
      { name: 'Templates', href: '/templates', icon: Files },
      { name: 'Compliance', href: '/compliance', icon: CheckSquare },
    ]
  },
  {
    section: 'Workspace',
    items: [
      { name: 'Tasks', href: '/tasks', icon: ListChecks },
      { name: 'Audit Trail', href: '/audit-logs', icon: ClockClockwise },
      { name: 'Settings', href: '/settings', icon: Gear },
    ]
  }
];

export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 min-h-screen flex flex-col glass-strong" style={{borderRight: '1px solid var(--border-glass)'}}>
          <div className="p-6 border-b" style={{borderColor: 'var(--border-glass)'}}>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg ocean-gradient flex items-center justify-center text-white font-bold text-lg shadow-md" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>S</div>
              <div>
                <h1 className="text-xl font-bold tracking-tight ocean-text" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="app-logo">SAIL Books</h1>
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Smooth Sails</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto p-4 space-y-5" data-testid="sidebar-navigation">
            {navigation.map((section) => (
              <div key={section.section}>
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] px-3 mb-2" style={{color: 'var(--text-muted)'}}>{section.section}</p>
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          isActive
                            ? 'ocean-gradient text-white shadow-md'
                            : 'text-slate-700 hover:bg-white/60 hover:text-slate-900'
                        }`}
                      >
                        <Icon size={18} weight={isActive ? 'fill' : 'regular'} />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div className="p-4 border-t" style={{borderColor: 'var(--border-glass)'}}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg ocean-gradient flex items-center justify-center text-white">
                <User size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate text-slate-900" data-testid="user-name">{user?.name}</p>
                <p className="text-xs capitalize truncate text-slate-500" data-testid="user-role">{user?.role}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              data-testid="logout-button"
              variant="outline"
              className="w-full justify-start gap-2 bg-white/50 border-slate-300 hover:bg-white hover:border-slate-900 transition-all"
            >
              <SignOut size={18} />
              Sign out
            </Button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
